import React, { useState, useCallback, useMemo } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';

import { ReactComponent as CalendarIcon } from 'assets/images/svg/calender-search-icon.svg';
import { ReactComponent as ChevronRight } from 'assets/images/svg/chevron-right.svg';
import { ReactComponent as Cross } from 'assets/images/svg/cross.svg';
import { ReactComponent as LocationIcon } from 'assets/images/svg/location.svg';
import { ReactComponent as Navigation } from 'assets/images/svg/navigation.svg';
import { ReactComponent as Reset } from 'assets/images/svg/filter-arrow.svg';
import { ReactComponent as Search } from 'assets/images/svg/search.svg';
import { BrandLogo } from 'assets';

import DatePicker from 'components/shared/DatePicker';
import StandardButton from 'components/shared/StandardButton';
import SearchAutocompleteMobile from 'components/shared/SearchAutocompleteMobile';

import { CURRENT_LOCATION } from 'utils/constants';
import siteStyle from 'utils/siteStyle';

const SearchFormMobile = ({
  currentLocation,
  date,
  setDate,
  location,
  setLocation,
  onSearch,
  isDirty,
  isFloatingSearchBar,
}) => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);
  const [isFirstStepDone, setIsFirstStepDone] = useState(false);

  const [value, setValue] = useState(location?.label ?? '');
  const [searchLocation, setSearchLocation] = useState(location?.label ?? '');
  const [searchDate, setSearchDate] = useState(date);

  const performNextStep = useCallback(() => {
    if (!isFirstStepDone) {
      if (currentModal === 'location') setCurrentModal('datepicker');
      if (currentModal === 'datepicker') setCurrentModal('location');
      setIsFirstStepDone(true);
      return;
    }
    onSearch(() => setIsSearchModalOpen('false'));
  }, [currentModal, isFirstStepDone, onSearch]);

  const openSearchModal = useCallback((modalName) => {
    setIsSearchModalOpen(true);
    setCurrentModal(modalName);
    setIsFirstStepDone(false);
  }, []);

  const currentLocationLabel = useMemo(() => {
    if (currentLocation?.city && currentLocation?.state) return `${currentLocation?.city}, ${currentLocation?.state}`;
    if (currentLocation?.city) return `${currentLocation?.city}`;
    return 'Current Location';
  }, [currentLocation]);

  const searchModal = (
    <ReactModal
      isOpen={isSearchModalOpen}
      className="fixed inset-0 flex flex-col bg-white outline-none text-4.5"
      bodyOpenClassName="overflow-hidden"
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
      onRequestClose={() => setIsSearchModalOpen(false)}
    >
      <div className="h-14 px-4 bg-white flex items-center justify-between">
        <button
          className="w-9 h-9 flex justify-center items-center focus:outline-none"
          onClick={() => {
            setIsSearchModalOpen(false);
            setCurrentModal('location');
            if (!searchLocation) setValue('');
          }}
        >
          <Cross className="w-5 h-5 fill-current text-dark" />
        </button>
        <BrandLogo className={siteStyle({ default: 'h-8', foh: 'w-50' })} />
        <div className="h-9 w-9" />
      </div>
      <div className="flex-grow">
        <div className={`${currentModal === 'datepicker' ? '' : 'hidden'}`}>
          <div className="px-4 py-4">
            <h3 className="font-bold leading-5 text-black font-primary text-5 -tracking-0.2">When are you playing?</h3>
            <h4 className="mt-2 font-normal leading-5 text-3.5 -tracking-0.2 text-grey-3">Select the day you want to play</h4>
          </div>
          <DatePicker
            value={searchDate}
            onChange={(val) => {
              setDate(val);
              setSearchDate(val);
            }}
          />
        </div>
        <div className={`${currentModal === 'location' ? '' : 'hidden'}`}>
          <div className="px-4 pt-4">
            <h3 className="font-bold leading-5 text-black font-primary text-5 -tracking-0.2">Where are you playing?</h3>
            <h4 className="mt-2 font-normal leading-5 text-3.5 -tracking-0.2 text-grey-3">Search by city, course, or zip code</h4>
          </div>
          <div className="px-4 mt-6">
            <SearchAutocompleteMobile
              value={value}
              setValue={setValue}
              setLocation={setLocation}
              currentLocationLabel={currentLocationLabel}
              setSearchLocation={setSearchLocation}
              initialAtCurrentLocation={false}
            />
          </div>
          {currentLocation.hierarchizedUrl && (
            <div
              className="flex items-center gap-4 px-4 pb-6 mt-8 border-b cursor-pointer border-light-grey-3"
              onClick={() => {
                setLocation({
                  ...CURRENT_LOCATION,
                  type: 'City',
                  hierarchizedUrl: currentLocation.hierarchizedUrl,
                  latitude: currentLocation.lat,
                  longitude: currentLocation.lon,
                  currentLocationSlug: currentLocation.slug,
                  currentLocationLabel: currentLocation.label,
                  currentLocationName: currentLocation.city,
                });
                setSearchLocation(currentLocation?.city);
                setValue(currentLocation?.city);
                performNextStep();
              }}
              aria-hidden
            >
              <div><Navigation /></div>
              <div>
                <div className="font-bold leading-5 text-4 -tracking-0.2 text-dark">Around me</div>
                <div className="leading-5 text-3.5 -tracking-0.2 text-grey-3">See courses near you</div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="fixed bottom-0 flex items-center w-full gap-4 p-4 bg-white border-t border-light-grey-3">
        <div className="h-14 w-14">
          <StandardButton
            isSecondary
            roundedStyle="rounded-3"
            heightStyle="h-14"
            widthStyle="w-14"
            onClick={() => {
              if (currentModal === 'datepicker') {
                setSearchDate(new Date());
              } else {
                setValue('');
                setSearchLocation('');
              }
            }}
            otherStyles="flex justify-center items-center"
            text={<Reset className="h-6 text-black fill-current w-5-5" />}
          />
        </div>
        <StandardButton
          text={!isFirstStepDone ? 'Next' : 'Search'}
          disabled={currentModal === 'location' && !searchLocation && !currentLocation?.city}
          onClick={performNextStep}
        />
      </div>
    </ReactModal>
  );

  const renderSearchLocation = (
    <div
      className={siteStyle({
        common: 'flex h-14 bg-white border border-light-grey-3 px-4 gap-2 cursor-pointer',
        default: 'rounded-3',
        foh: 'rounded-2',
        gd: 'rounded-none',
      })}
      onClick={() => openSearchModal('location')}
      aria-hidden
    >
      <div className="flex items-center">
        <LocationIcon className="w-4 h-4 text-dark fill-current" />
      </div>
      <div className="flex flex-col justify-center" style={{ maxWidth: 'calc(100% - 16px)' }}>
        {!isFloatingSearchBar && (
          <div
            className={siteStyle({
              common: 'text-3.5 leading-4 -tracking-0.2 text-grey-3',
              // bt: 'font-korolav uppercase',
              foh: 'font-roboto',
              default: 'font-primary-two',
            })}
          >
            Where to?
          </div>
        )}
        <div className={classnames(siteStyle({ common: 'font-medium text-4 leading-5 -tracking-0.2 truncate', default: 'font-primary', foh: 'font-roboto' }), {
          'text-dark': searchLocation || currentLocation?.city,
          'text-grey-3': !(searchLocation || currentLocation?.city),
        })}
        >
          {searchLocation || currentLocationLabel || 'Any course, city, or zip'}
        </div>
      </div>
    </div>
  );

  const renderSearchDate = (
    <div
      className={siteStyle({
        common: 'flex h-14 bg-white border border-light-grey-3 px-4 gap-2 cursor-pointer',
        default: 'rounded-3',
        foh: 'rounded-2',
        gd: 'rounded-none',
      })}
      onClick={() => openSearchModal('datepicker')}
      aria-hidden
    >
      <div className="flex items-center">
        <CalendarIcon className="w-4 h-4 fill-current text-dark" />
      </div>
      <div className="flex flex-col justify-center flex-grow">
        {!isFloatingSearchBar
          && (
            <div
              className={siteStyle({
                common: 'text-3.5 leading-4 -tracking-0.2 text-grey-3',
                // bt: 'font-korolav uppercase',
                foh: 'font-roboto',
                default: 'font-primary-two',
              })}
            >
              Date
            </div>
          )}
        <div className={siteStyle({ common: 'font-medium text-4 leading-5 text-dark -tracking-0.2', default: 'font-primary', foh: 'font-roboto' })}>{moment(searchDate).format('MMM D')}</div>
      </div>
      <div className="flex items-center gap-8">
        <button
          type="button"
          className="border-none focus:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            if (moment(searchDate).isBefore(moment(new Date()))) return;
            setSearchDate(moment(searchDate).add(-1, 'day').toDate());
            setDate(moment(searchDate).add(-1, 'day').toDate());
          }}
        >
          <ChevronRight className={classnames('rotate-180 h-4 w-4 fill-current text-dark', {
            'text-light-grey-blue-two': moment(searchDate).isBefore(moment(new Date())),
          })}
          />
        </button>
        <button
          type="button"
          className="border-none focus:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            setSearchDate(moment(searchDate).add(1, 'day').toDate());
            setDate(moment(searchDate).add(1, 'day').toDate());
          }}
        >
          <ChevronRight className="w-4 h-4 fill-current text-dark" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {searchModal}
      <div className="flex flex-col gap-2">
        {renderSearchLocation}
        {renderSearchDate}
        <StandardButton
          disabled={!searchLocation && !currentLocation?.city}
          text={(
            <div
              className={siteStyle({
                common: 'flex items-center justify-center gap-2',
                // bt: 'font-korolav uppercase',
                foh: 'font-rajdhani',
                default: 'font-primary',
              })}
            >
              <Search style={{ transform: 'rotateY(180deg)' }} className="w-4 h-4 text-white fill-current" />
              {!isFloatingSearchBar
                ? <p className="font-bold text-white -tracking-0.25 text-4 leading-4">{isDirty ? 'Update' : 'Search'}</p>
                : <p className="font-bold text-white -tracking-0.25 text-4 leading-4">Update search</p>}
            </div>
          )}
          heightStyle="h-14"
          onClick={() => onSearch(() => setIsSearchModalOpen(false))}
        />
      </div>
    </>
  );
};

SearchFormMobile.defaultProps = {
  currentLocation: null,
  location: null,
  onSearch: null,
  isDirty: false,
  date: new Date(),
  isFloatingSearchBar: false,
};

SearchFormMobile.propTypes = {
  currentLocation: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
    hierarchizedUrl: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    slug: PropTypes.string,
    label: PropTypes.string,
  }),
  date: PropTypes.instanceOf(Date),
  location: PropTypes.shape({ label: PropTypes.string }),
  isDirty: PropTypes.bool,
  isFloatingSearchBar: PropTypes.bool,
  onSearch: PropTypes.func,
  setDate: PropTypes.func.isRequired,
  setLocation: PropTypes.func.isRequired,
};

export default SearchFormMobile;
